import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { Save, Send, ArrowLeft, Plus, X, Upload, FileText, Trash2, CheckCircle } from 'lucide-react';

const TABS = [
  { id: 'general', label: 'Genel Bilgiler' },
  { id: 'files', label: 'Dosyalar' },
  { id: 'versions', label: 'Versiyonlar' },
  { id: 'visibility', label: 'Görünürlük' }
];

const CATEGORIES = [
  'Plan Örneği', 'Etkinlik', 'Özel Gün Takvimi', 
  'Okuma Listesi', 'Kılavuz', 'Politika', 'Diğer'
];

const TARGET_AUDIENCES = [
  { value: 'TEACHER', label: 'Öğretmenler' },
  { value: 'COORDINATOR', label: 'Koordinatörler' },
  { value: 'PRINCIPAL', label: 'Yöneticiler' },
  { value: 'TRAINER', label: 'Eğitmenler' },
  { value: 'PARENT', label: 'Veliler' }
];

const LibraryDocumentEditor = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Plan Örneği',
    tags: [],
    targetAudience: [],
    visibilityScope: 'Tüm Okullar',
    allowedOrganizationIds: [],
    language: 'tr'
  });
  
  const [newTag, setNewTag] = useState('');
  const [showVersionModal, setShowVersionModal] = useState(false);

  // Load organizations for visibility selection
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setOrganizations(data);
        }
      } catch (error) {
        console.error('Okullar yüklenemedi:', error);
      }
    };
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (!user?.email?.endsWith('@yomev.org')) {
      navigate('/admin/library');
      return;
    }

    if (documentId && documentId !== 'new') {
      loadDocument();
    } else {
      setLoading(false);
    }
  }, [documentId, user, navigate]);

  // Helper to check if this is a new document
  const isNewDocument = !documentId || documentId === 'new';

  const loadDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/documents/${documentId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDocument(data);
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || 'Plan Örneği',
          tags: data.tags || [],
          targetAudience: data.targetAudience || [],
          visibilityScope: data.visibilityScope || 'Tüm Okullar',
          allowedOrganizationIds: data.allowedOrganizationIds || [],
          language: data.language || 'tr'
        });
        
        // Load versions
        const versionsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/library/documents/${documentId}/versions`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (versionsResponse.ok) {
          const versionsData = await versionsResponse.json();
          setVersions(versionsData);
        }
      }
    } catch (error) {
      console.error('Doküman yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      alert('Doküman başlığı zorunludur');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let savedDocId = documentId;
      
      // Step 1: Create or update document
      const url = !isNewDocument
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/documents/${documentId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/documents`;
      
      const method = !isNewDocument ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Doküman kaydedilemedi');
      }
      
      const savedDoc = await response.json();
      savedDocId = savedDoc.id;
      
      // Step 2: If there are files and this is a new document, create first version with files
      if (selectedFiles.length > 0 && isNewDocument) {
        setUploading(true);
        
        // First create the version
        const versionResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/documents/${savedDocId}/versions`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              libraryDocumentId: savedDocId,
              changeLog: 'İlk versiyon',
              fileAssetIds: []
            })
          }
        );
        
        if (!versionResponse.ok) {
          throw new Error('Versiyon oluşturulamadı');
        }
        
        const version = await versionResponse.json();
        
        // Now upload files
        for (const file of selectedFiles) {
          setUploadProgress(prev => ({ ...prev, [file.name]: 'uploading' }));
          
          const fileFormData = new FormData();
          fileFormData.append('file', file);
          fileFormData.append('document_version_id', version.id);
          
          try {
            const uploadResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/upload`,
              {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fileFormData
              }
            );
            
            if (uploadResponse.ok) {
              setUploadProgress(prev => ({ ...prev, [file.name]: 'success' }));
            } else {
              setUploadProgress(prev => ({ ...prev, [file.name]: 'error' }));
            }
          } catch (err) {
            setUploadProgress(prev => ({ ...prev, [file.name]: 'error' }));
          }
        }
        
        setUploading(false);
      }
      
      alert('Doküman başarıyla kaydedildi');
      
      if (isNewDocument) {
        window.location.href = `/admin/library/documents/${savedDocId}/edit`;
      } else {
        loadDocument();
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('Doküman kaydedilirken hata oluştu: ' + error.message);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['ppt', 'pptx'].includes(ext)) return '📊';
    if (['xls', 'xlsx'].includes(ext)) return '📈';
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
    if (['mp4', 'avi', 'mov'].includes(ext)) return '🎬';
    return '📎';
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const toggleAudience = (value) => {
    const current = formData.targetAudience;
    setFormData({
      ...formData,
      targetAudience: current.includes(value)
        ? current.filter(a => a !== value)
        : [...current, value]
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Kütüphane Yönetimi', path: '/admin/library' },
          { label: documentId === 'new' ? 'Yeni Doküman' : 'Doküman Düzenle', path: null }
        ]} 
      />

      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/admin/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Geri
        </button>
        
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Save size={18} />
            Kaydet
          </button>
          {document && (
            <button
              onClick={() => setShowVersionModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              Yeni Versiyon
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlık *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Örn: 5-6 Yaş Ekoloji Temalı Haftalık Plan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Doküman hakkında detaylı açıklama..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Etiketler
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Etiket ekle (Enter ile)"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Ekle
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full"
                    >
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-blue-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hedef Kitle
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {TARGET_AUDIENCES.map(audience => (
                    <label key={audience.value} className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.targetAudience.includes(audience.value)}
                        onChange={() => toggleAudience(audience.value)}
                        className="rounded text-blue-600"
                      />
                      <span>{audience.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dosya Yükle
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload-main"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.zip"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload-main" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="text-gray-400" size={48} />
                      <span className="text-lg text-gray-600">Dosya seçmek için tıklayın veya sürükleyin</span>
                      <span className="text-sm text-gray-500">PDF, Word, PowerPoint, Excel, Görsel, Video (Maks. 50MB)</span>
                    </div>
                  </label>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Seçili Dosyalar ({selectedFiles.length})</h4>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getFileIcon(file.name)}</span>
                          <div>
                            <div className="font-medium text-gray-900">{file.name}</div>
                            <div className="text-sm text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                              {uploadProgress[file.name] && (
                                <span className={`ml-2 ${
                                  uploadProgress[file.name] === 'success' ? 'text-green-600' : 
                                  uploadProgress[file.name] === 'error' ? 'text-red-600' : 'text-blue-600'
                                }`}>
                                  {uploadProgress[file.name] === 'uploading' && '⏳ Yükleniyor...'}
                                  {uploadProgress[file.name] === 'success' && '✓ Yüklendi'}
                                  {uploadProgress[file.name] === 'error' && '✗ Hata'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelectedFile(index)}
                          disabled={uploading}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isNewDocument && selectedFiles.length === 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>İpucu:</strong> Yeni doküman oluşturuyorsunuz. Dosya ekleyip "Kaydet" butonuna bastığınızda 
                    doküman otomatik olarak ilk versiyon ile birlikte oluşturulacaktır.
                  </p>
                </div>
              )}

              {!isNewDocument && selectedFiles.length === 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    📁 Mevcut dokümana yeni dosya eklemek için "Yeni Versiyon" butonunu kullanın.
                    Bu sayede eski versiyonlar da kayıt altında tutulur.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'versions' && (
            <div>
              {versions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Henüz versiyon eklenmemiş</p>
                  <p className="text-sm text-gray-500 mt-2">Önce dokümanı kaydedin, sonra versiyon ekleyebilirsiniz</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div key={version.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">v{version.versionNumber}</span>
                          {version.isPublishedVersion && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                              Yayında
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(version.createdAt).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      {version.changeLog && (
                        <p className="text-sm text-gray-600 mt-2 italic">"{version.changeLog}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'visibility' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Görünürlük
                </label>
                <div className="space-y-3">
                  {['Tüm Okullar', 'Belirli Okullar', 'Sadece YÖMEV'].map((scope) => (
                    <label key={scope} className="flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.visibilityScope === scope}
                        onChange={() => setFormData({...formData, visibilityScope: scope, allowedOrganizationIds: scope !== 'Belirli Okullar' ? [] : formData.allowedOrganizationIds})}
                        className="text-blue-600"
                      />
                      <div>
                        <div className="font-medium">{scope}</div>
                        <div className="text-sm text-gray-600">
                          {scope === 'Tüm Okullar' && 'Tüm akredite okullar bu dokümana erişebilir'}
                          {scope === 'Belirli Okullar' && 'Sadece seçili okullar erişebilir'}
                          {scope === 'Sadece YÖMEV' && 'Sadece YÖMEV ekibi görebilir'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Organization Selection for "Belirli Okullar" */}
                {formData.visibilityScope === 'Belirli Okullar' && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Erişim İzni Verilecek Okullar ({formData.allowedOrganizationIds.length} seçili)
                    </label>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {organizations.map((org) => (
                        <label 
                          key={org.id} 
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            formData.allowedOrganizationIds.includes(org.id) 
                              ? 'bg-blue-100 border-blue-300 border' 
                              : 'bg-white border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.allowedOrganizationIds.includes(org.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData, 
                                  allowedOrganizationIds: [...formData.allowedOrganizationIds, org.id]
                                });
                              } else {
                                setFormData({
                                  ...formData, 
                                  allowedOrganizationIds: formData.allowedOrganizationIds.filter(id => id !== org.id)
                                });
                              }
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{org.name}</div>
                            <div className="text-sm text-gray-500">{org.city}</div>
                          </div>
                          {org.accreditationLevel && (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                              {org.accreditationLevel}
                            </span>
                          )}
                        </label>
                      ))}
                      {organizations.length === 0 && (
                        <p className="text-gray-500 text-center py-4">Kayıtlı okul bulunamadı</p>
                      )}
                    </div>
                    {formData.allowedOrganizationIds.length === 0 && (
                      <p className="text-sm text-orange-600 mt-2">
                        ⚠️ En az bir okul seçmelisiniz
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Version Modal */}
      {showVersionModal && (
        <VersionModal
          documentId={documentId}
          onClose={() => setShowVersionModal(false)}
          onSuccess={() => {
            setShowVersionModal(false);
            loadDocument();
          }}
        />
      )}
    </div>
  );
};

// Version Modal Component
const VersionModal = ({ documentId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    changeLog: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.changeLog.trim()) {
      alert('Değişiklik notu zorunludur');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      let uploadedFileIds = [];

      // Upload files first
      if (selectedFiles.length > 0) {
        setUploading(true);
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          setUploadProgress(prev => ({ ...prev, [file.name]: 'uploading' }));
          
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/upload`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                body: formData
              }
            );
            
            if (uploadResponse.ok) {
              const uploadedFile = await uploadResponse.json();
              uploadedFileIds.push(uploadedFile.id);
              setUploadProgress(prev => ({ ...prev, [file.name]: 'success' }));
            } else {
              setUploadProgress(prev => ({ ...prev, [file.name]: 'error' }));
              throw new Error(`${file.name} yüklenemedi`);
            }
          } catch (error) {
            console.error(`Dosya yükleme hatası (${file.name}):`, error);
            alert(`Dosya yüklenirken hata oluştu: ${file.name}`);
            setSubmitting(false);
            setUploading(false);
            return;
          }
        }
        setUploading(false);
      }
      
      // Create version with uploaded file IDs
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/documents/${documentId}/versions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            libraryDocumentId: documentId,
            changeLog: formData.changeLog,
            fileAssetIds: uploadedFileIds
          })
        }
      );
      
      if (response.ok) {
        alert('Yeni versiyon başarıyla oluşturuldu!');
        onSuccess();
      } else {
        alert('Versiyon oluşturulurken hata oluştu');
      }
    } catch (error) {
      console.error('Versiyon oluşturma hatası:', error);
      alert('Versiyon oluşturulurken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['ppt', 'pptx'].includes(ext)) return '📊';
    if (['xls', 'xlsx'].includes(ext)) return '📈';
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
    if (['mp4', 'avi', 'mov'].includes(ext)) return '🎬';
    return '📎';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">Yeni Versiyon Ekle</h2>
          <p className="text-sm text-gray-600 mt-1">Dokümanın yeni bir versiyonunu oluşturun ve dosyaları yükleyin</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Değişiklik Notu *
            </label>
            <input
              type="text"
              value={formData.changeLog}
              onChange={(e) => setFormData({...formData, changeLog: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: 2025 tarihleri ile güncellendi, yeni bölüm eklendi"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dosyalar
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.zip"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <Plus className="text-gray-400" size={40} />
                  <span className="text-sm text-gray-600">Dosya seçmek için tıklayın</span>
                  <span className="text-xs text-gray-500">PDF, Word, PowerPoint, Excel, Görsel, Video</span>
                </div>
              </label>
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Seçili Dosyalar ({selectedFiles.length})</h4>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFileIcon(file.name)}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{file.name}</div>
                        <div className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                          {uploadProgress[file.name] && (
                            <span className={`ml-2 ${uploadProgress[file.name] === 'success' ? 'text-green-600' : uploadProgress[file.name] === 'error' ? 'text-red-600' : 'text-blue-600'}`}>
                              {uploadProgress[file.name] === 'uploading' && '⏳ Yükleniyor...'}
                              {uploadProgress[file.name] === 'success' && '✓ Yüklendi'}
                              {uploadProgress[file.name] === 'error' && '✗ Hata'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={uploading || submitting}
                      className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting || uploading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? '📤 Dosyalar Yükleniyor...' : submitting ? 'Oluşturuluyor...' : 'Versiyon Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LibraryDocumentEditor;
